import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { ExportActions, GridService, NotificationService } from '@core/services';
import { errorDetail } from '@util/http';

import { AdminService } from '@admin/services';
import { AuthActions } from '@auth/state';

import * as AdminActions from './admin.actions';

@Injectable()
export class AdminEffects {
  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private actions$: Actions,
    private translocoService: TranslocoService,
    private adminService: AdminService,
    private notificationService: NotificationService,
    private gridService: GridService
  ) {}

  InquiryRequestDataForExport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminActions.LookupRequestDataForExport),
      concatLatestFrom(({ id }) => [this.gridService.filters$(id), this.gridService.sortsObject$(id)]),
      exhaustMap(([{ id, format, rows }, filters, sorts]) =>
        this.adminService.userLookup({ filters, page: 1, rows, sorts }).pipe(
          map((data) => ExportActions.RequestDataForExportSuccess({ id, format, data })),
          catchError((error) => of(ExportActions.RequestDataForExportFailure({ id, error })))
        )
      )
    );
  });

  Initialize$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AdminActions.CreateUserInitialize,
        AdminActions.DetailInitialize,
        AdminActions.LookupInitialize,
        AdminActions.UploadImageInitialize
      ),
      map((screen) => AuthActions.ChangeScreen(screen))
    );
  });

  UploadImageRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminActions.UploadImageRequest),
      exhaustMap(({ formData }) =>
        this.adminService.uploadImage(formData).pipe(
          map((data) => AdminActions.UploadImageRequestSuccess({ data })),
          catchError((error) => of(AdminActions.UploadImageRequestFailure({ error })))
        )
      )
    );
  });

  UploadImageRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AdminActions.UploadImageRequestSuccess),
        map(() =>
          this.notificationService.info({
            title: this.translate('admin.image_upload.notifications.image_uploaded.title'),
            message: this.translate('admin.image_upload.notifications.image_uploaded.message'),
          })
        ),
        map(() => this.adminService.imageUploaded$.next())
      );
    },
    { dispatch: false }
  );

  UploadImageRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AdminActions.UploadImageRequestFailure),
        map(() => this.adminService.imageUploaded$.next())
      );
    },
    { dispatch: false }
  );

  DeleteImageRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminActions.DeleteImageRequest),
      exhaustMap(({ imageType }) =>
        this.adminService.deleteImage({ type: imageType }).pipe(
          map((data) => AdminActions.DeleteImageRequestSuccess({ data })),
          catchError((error) => of(AdminActions.DeleteImageRequestFailure({ error })))
        )
      )
    );
  });

  DeleteImageRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AdminActions.DeleteImageRequestSuccess),
        map(() =>
          this.notificationService.info({
            title: this.translate('admin.image_upload.notifications.image_deleted.title'),
            message: this.translate('admin.image_upload.notifications.image_deleted.message'),
          })
        ),
        map(() => this.adminService.imageDeleted$.next())
      );
    },
    { dispatch: false }
  );

  DeleteImageRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AdminActions.DeleteImageRequestFailure),
        map(() => this.adminService.imageDeleted$.next())
      );
    },
    { dispatch: false }
  );

  LookupRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminActions.LookupRequestData, AdminActions.LookupRequestDataWithParameters),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.adminService.userLookup({ filters, page, rows, sorts }).pipe(
          map((data) => AdminActions.LookupRequestDataSuccess({ id, data })),
          catchError((_error) => of(AdminActions.LookupRequestDataFailure({ id })))
        )
      )
    );
  });

  DetailRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminActions.DetailRequestData),
      exhaustMap(({ nameid }) =>
        this.adminService.userDetail({ nameid }).pipe(
          map((data) => AdminActions.DetailRequestDataSuccess({ nameid, data })),
          catchError((_error) => of(AdminActions.DetailRequestDataFailure()))
        )
      )
    );
  });

  UpdateUserRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminActions.UpdateUserRequest),
      exhaustMap(({ data }) =>
        this.adminService.updateUser(data).pipe(
          map((response) => AdminActions.UpdateUserRequestSuccess({ data: response })),
          catchError((error) => of(AdminActions.UpdateUserRequestFailure({ error })))
        )
      )
    );
  });

  UpdateUserRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AdminActions.UpdateUserRequestSuccess),
        map(() => {
          this.notificationService.success({
            message: this.translate('admin.users.detail.success'),
          });

          this.adminService.userChangePending$.next(true);
        })
      );
    },
    { dispatch: false }
  );

  UpdateUserRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AdminActions.UpdateUserRequestFailure),
        map(({ error }) => {
          const detail = errorDetail(error);

          let message = detail.message;

          if (message === 'invalid-permission') {
            message = this.translate('admin.users.detail.errors.invalid_permission');
          }

          if (message === 'invalid-customer') {
            message = this.translate('admin.users.detail.errors.invalid_customer');
          }

          if (message === 'invalid-facility') {
            message = this.translate('admin.users.detail.errors.invalid_facility');
          }

          if (message === 'invalid-report') {
            message = this.translate('admin.users.detail.errors.invalid_report');
          }

          if (message === 'passwords-do-not-match') {
            message = this.translate('admin.users.detail.errors.passwords_do_not_match');
          }

          this.notificationService.error({ message });
        })
      );
    },
    { dispatch: false }
  );

  CreateUserRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminActions.CreateUserRequest),
      exhaustMap(({ username, password }) =>
        this.adminService.createUser({ username, password }).pipe(
          map((data) => AdminActions.CreateUserRequestSuccess({ username, password, data })),
          catchError((error) => of(AdminActions.CreateUserRequestFailure({ error })))
        )
      )
    );
  });

  CreateUserRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AdminActions.CreateUserRequestSuccess),
        map(({ username, data }) => {
          this.notificationService.success({
            id: 'user-created',
            message: this.translate('admin.users.create.success', { nameid: username }),
            payload: {
              username,
              data,
            },
          });

          this.adminService.userChangePending$.next(true);
        })
      );
    },
    { dispatch: false }
  );

  CreateUserRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AdminActions.CreateUserRequestFailure),
        map(({ error }) => {
          const detail = errorDetail(error);

          let message = detail.message;

          if (message === 'user-already-exists') {
            message = this.translate('admin.users.create.errors.user_already_exists');
          }

          if (message === 'passwords-do-not-match') {
            message = this.translate('admin.users.create.errors.passwords_do_not_match');
          }

          this.notificationService.error({ message });
        })
      );
    },
    { dispatch: false }
  );
}
